import type { SettingsMenuContainer } from '../settings-menu-container'

import { Events } from '|>/shared/events'
import { bind_ } from '|>/shared/fn'
import { textContent } from '|>/shared/h'
import { on, register } from '|>/shared/vjs'
import { BaseMenuButton } from '../../base'
import { LocalEvents } from '../local-events'
import {
  formatQualityLevels,
  getQualityItemPixels,
  getQualityLabel,
  isAutoQuality,
  isAutoQualityPixelsEnbled,
} from './helpers'
import { QualityMenuItem } from './quality-menu-item'

@register
export class QualityButton extends BaseMenuButton {
  declare options_: any
  declare menu: NonNullable<InstanceType<typeof BaseMenuButton>['menu']>
  declare container: SettingsMenuContainer | undefined
  declare maxAutoQuality: Events.Media.MaxAutoQuality
  isSelectedQualityAllowed: Events.Player.IsSelectedQualityAllowed | undefined
  isVideoQualityBlocked: Events.Player.IsVideoQualityBlocked | undefined
  isLoadedMetadata: boolean = false

  constructor(player, options) {
    super(player, options)

    // Default threshold for hiding when only one item exists
    this.hideThreshold_ = 1

    const items = this.children() // `this` is Menu instance

    if (items.length <= 1) {
      this.hide()
    }

    this.handleMenuTitleClick = bind_(this, this.handleMenuTitleClick)

    this.container = options.getContainer?.()
  }

  @on(Events.Player.IsSelectedQualityAllowed)
  onIsSelectedQualityAllowed(
    isSelectedQualityAllowed: Events.Player.IsSelectedQualityAllowed
  ) {
    this.isSelectedQualityAllowed = isSelectedQualityAllowed
  }

  @on(Events.Player.IsVideoQualityBlocked)
  onIsVideoQualityBlocked(
    isVideoQualityBlocked: Events.Player.IsVideoQualityBlocked
  ) {
    this.isVideoQualityBlocked = isVideoQualityBlocked
  }

  @on(Events.Media.MaxAutoQuality)
  onMaxAutoQuality(maxAutoQuality: Events.Media.MaxAutoQuality) {
    this.maxAutoQuality = maxAutoQuality
  }

  @on('loadedmetadata')
  onLoadedMetadata() {
    this.isLoadedMetadata = true

    const qualityList = this.player_.qualityLevels()

    let minVideoQuality: number | null = null

    if (qualityList) {
      const isAuto = isAutoQuality(qualityList)

      if (isAuto) {
        for (let i = 0; i < qualityList.length; ++i) {
          const level = qualityList[i]
          const pixels = getQualityItemPixels(level)
          if (!pixels) continue

          if (minVideoQuality === null || pixels < minVideoQuality) {
            minVideoQuality = pixels
          }

          level.enabled = isAutoQualityPixelsEnbled({
            pixels,
            maxAutoQuality: this.maxAutoQuality,
            isSelectedQualityAllowed: this.isSelectedQualityAllowed,
          })
        }
      }

      if (minVideoQuality !== null) {
        this.player_.isBlocked = this.isVideoQualityBlocked?.({
          quality: minVideoQuality,
        })

        if (this.player_.isBlocked) {
          this.player_.setBlockedQualityLevels(qualityList)
        }
      }
    }

    this.updateQualitiesMenu()
  }

  updateQualitiesMenu() {
    const container = this.container

    if (!container) return

    container.qualityMenu.update()

    if (this.menu.hasClass('vjs-x-active')) {
      container.el_.style.height = `${this.menu.el_.scrollHeight}px`
    }
  }

  override update(forceUpdate: boolean = false) {
    super.update()

    this.container = this.options_.getContainer?.()

    this.menu.addClass(
      `vjs-x-settings-quality-menu ${forceUpdate && 'vjs-x-active'}`
    )

    const that = this
    this.menu.handleTapClick = function (event) {
      const items = this.children() // `this` is Menu instance
      const item = items?.find((it) => it.el() === event.target)
      if (item) {
        if (item.hasClass('vjs-menu-title')) {
          that.handleMenuTitleClick()
        }
      }
    }
  }

  title(title: string) {
    if (this.menu) {
      const el_ = this.menu.$('.vjs-menu-title') as HTMLElement
      if (el_) {
        textContent(el_, title)
      }
    }
  }

  handleMenuTitleClick() {
    const container = this.options_.getContainer()
    container?.trigger(LocalEvents.SettingsActivateMainMenu)
  }

  getQualityState() {
    return this.container?.qualityState
  }

  override createItems(items: QualityMenuItem[] = []) {
    let qualityLevels = this.isLoadedMetadata
      ? this.player_.qualityLevels()
      : null

    // player quality level is blocked by limitations, use saved quality levels
    if (this.player_.isBlocked)
      qualityLevels = this.player_.blockedQualityLevels

    if (!qualityLevels?.length) return []

    const qualityLevelsSorted = formatQualityLevels(qualityLevels.levels_)

    const currentQuality =
      this.player_.qualityLevels()[qualityLevels.selectedIndex_]

    const qualityState = this.getQualityState()

    if (!qualityState) return []

    qualityLevelsSorted.forEach((level, index) => {
      if (index === 0) {
        let label = this.localize('quality_auto')
        if (qualityState.id === 'auto' && currentQuality) {
          label = `${label} (${getQualityLabel({
            quality: currentQuality,
            isAuto: true,
          })})`
        }

        items.push(
          new QualityMenuItem(this.player_, {
            selected: qualityState.id === 'auto',
            label,
            isAuto: true,
            selectable: true,
            multiSelectable: false,
            maxAutoQuality: this.maxAutoQuality,
            isSelectedQualityAllowed: this.isSelectedQualityAllowed,
          })
        )
      }

      items.push(
        new QualityMenuItem(this.player_, {
          selected: qualityState.id === level.id,
          label: getQualityLabel({ quality: level }),
          level,
          selectable: true,
          multiSelectable: false,
          isSelectedQualityAllowed: this.isSelectedQualityAllowed,
        })
      )
    })

    return items
  }

  @on(['playerreset', 'dispose'])
  onPlaybackEnded() {
    this.isLoadedMetadata = false
  }

  @on('playerreset')
  onReset() {
    // in case of blocked by limitations playback, clear 'stale' quality list
    this.updateQualitiesMenu()
  }
}
